/**
 * Kiosk session helper — stores/retrieves kiosk_token + event in localStorage
 * so the kiosk survives a hard refresh.
 */
import { api } from "./nailApi";

const KEY = "nailEvent.kioskSession";

export function saveKioskSession({ kiosk_token, event, organizer, expires_at, permissions }) {
  const payload = { kiosk_token, event, organizer, expires_at, permissions, saved_at: new Date().toISOString() };
  try { localStorage.setItem(KEY, JSON.stringify(payload)); } catch (_) { /* ignore */ }
  return payload;
}

export function readKioskSession() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data?.kiosk_token) return null;
    if (data.expires_at && new Date(data.expires_at) <= new Date()) {
      localStorage.removeItem(KEY);
      return null;
    }
    return data;
  } catch (_) { return null; }
}

export function clearKioskSession() {
  try { localStorage.removeItem(KEY); } catch (_) { /* ignore */ }
}

export async function kioskLogin({ event_code, event_slug, pin, link_token }) {
  const { data } = await api.post("/kiosk/login", { event_code, event_slug, pin, link_token });
  return saveKioskSession(data);
}

export async function refreshKioskSession() {
  const s = readKioskSession();
  if (!s) return null;
  try {
    const { data } = await api.get("/kiosk/session", { headers: { "X-Kiosk-Token": s.kiosk_token } });
    return saveKioskSession({ ...s, event: data.event, organizer: data.organizer, permissions: data.permissions, expires_at: data.expires_at });
  } catch (_) {
    clearKioskSession();
    return null;
  }
}

export async function kioskLogout() {
  const s = readKioskSession();
  if (!s) return;
  try {
    await api.post("/kiosk/logout", {}, { headers: { "X-Kiosk-Token": s.kiosk_token } });
  } catch (_) { /* ignore network errors */ }
  clearKioskSession();
}

export async function kioskCheckin({ phone, language }) {
  const s = readKioskSession();
  if (!s) throw new Error("Kiosk session not found");
  const { data } = await api.post(
    "/kiosk/checkin",
    { phone, language },
    { headers: { "X-Kiosk-Token": s.kiosk_token } },
  );
  return data;
}

/**
 * Door kiosk lookup (2026-07-27). Locked-down endpoint for the venue entrance:
 * exact 10-digit only, document gate enforced server-side, response shape is
 * intentionally minimal (see backend routes/kiosk.py:_door_dict).
 */
export async function doorLookup({ phone, language }) {
  const s = readKioskSession();
  if (!s) throw new Error("Kiosk session not found");
  const { data } = await api.post(
    "/kiosk/door/lookup",
    { phone, language },
    { headers: { "X-Kiosk-Token": s.kiosk_token } },
  );
  return data;
}
